<script>
import CopySliceToPage from "@voix/components/chrome/slices/CopySliceToPage.vue";
import { useAdminStore } from "@voix/store/adminStore";
import { usePageStore } from "@voix/store/pageStore";
import { useUserStore } from "@voix/store/userStore";
import { computed, defineAsyncComponent, defineComponent, onMounted, ref } from "vue";
import VoixDrop from "../drag-and-drop/VoixDrop.vue";
import PageNavigationQuery from "./PageNavigationQuery.vue";
import PageNavigationSlice from "./PageNavigationSlice.vue";
const PageNavigationGroup = defineAsyncComponent(() => {
  return new Promise((resolve) => {
    const PageNavigationGroup2 = import("./PageNavigationGroup.vue");
    resolve(PageNavigationGroup2);
  });
});
export default defineComponent({
  name: "PageNavigationElements",
  components: {
    PageNavigationSlice,
    PageNavigationQuery,
    PageNavigationGroup,
    VoixDrop,
    CopySliceToPage
  },
  props: {
    parent: {
      type: Object,
      default: null
      // null is the root (or page)
    },
    slotName: {
      type: String,
      default: "default"
    },
    elements: {
      type: Array,
      required: true
    },
    depth: {
      type: Number,
      default: 1
    }
  },
  setup(props) {
    const pageStore = usePageStore();
    const adminStore = useAdminStore();
    const userStore = useUserStore();
    const currentUser = userStore.currentUser;
    const showAction = ref("");
    function contextMenuHandler(event, item) {
      event.preventDefault();
      event.stopPropagation();
      adminStore.currentContextItem.element = item;
      adminStore.currentContextItem.x = event.clientX;
      adminStore.currentContextItem.y = event.clientY;
    }
    function unsetContextMenu() {
      adminStore.currentContextItem.element = null;
    }
    const selectElement = (element) => {
      unsetContextMenu();
      adminStore.setSelectedElement(element);
    };
    const multiSelectElement = (element) => {
      unsetContextMenu();
      if (adminStore.selectedElements.length > 0) {
        if (adminStore.selectedElements.includes(element)) {
          adminStore.removeFromSelectedElements(element);
        } else {
          adminStore.addToSelectedElements(element);
        }
      } else {
        selectElement(element);
      }
    };
    const openElement = (element) => {
      unsetContextMenu();
      pageStore.openElement(element);
    };
    const closeElement = (element) => {
      unsetContextMenu();
      pageStore.closeElement(element);
    };
    const openAndSelect = (adminStoreElement) => {
      const element = pageStore.findElement(adminStoreElement.id);
      if (element) {
        pageStore.openElement(adminStoreElement);
        adminStore.setSelectedElement(element.element);
      } else {
        console.error("Element not found");
      }
      unsetContextMenu();
    };
    const findElement = (element) => {
      unsetContextMenu();
      adminStore.scrollToElement = element;
    };
    const makeRecipeFromElement = (element) => {
      unsetContextMenu();
      adminStore.scrollToElement = element;
      adminStore.makeRecipeFromElement(element);
    };
    const makeRecipeFromPage = () => {
      unsetContextMenu();
      adminStore.makeRecipeFromPage();
    };
    const setSliceElement = (element) => {
      return element;
    };
    const setGroupElement = (element) => {
      return element;
    };
    const setQuerySliceElement = (element) => {
      return element;
    };
    onMounted(() => {
      const channel = new BroadcastChannel("page-navigation-select-element");
      channel.onmessage = (data) => {
        adminStore.setSelectedElement(data.element);
      };
    });
    function dropTopOfDataArray(id) {
      if (props.parent)
        pageStore.moveElementIntoElement(id, props.parent.id);
      else
        pageStore.moveElement(id, null);
      pageStore.setSlot(id, props.slotName);
    }
    function dropMiddleOfDataArray(id, destinationId) {
      if (id !== destinationId) {
        pageStore.moveElement(id, destinationId);
        pageStore.setSlot(id, props.slotName);
      }
    }
    function duplicateElement(element) {
      unsetContextMenu();
      pageStore.duplicateElement(element.id);
    }
    function cancelCopyElementToPage() {
      showAction.value = "";
      unsetContextMenu();
    }
    function enableElement(element) {
      unsetContextMenu();
      pageStore.enableElement(element.id);
    }
    function disableElement(element) {
      unsetContextMenu();
      pageStore.disableElement(element.id);
    }
    function deleteElement(element) {
      unsetContextMenu();
      pageStore.deleteElement(element.id);
    }
    const canEditPageSettings = computed(() => {
      userStore?.currentUser?.all_permissions.forEach((permission) => {
        if (permission.name.toLowerCase() === "pages.*")
          return true;
        if (permission.name.toLowerCase() === "pages.edit.*")
          return true;
        if (permission.name.toLowerCase() === `pages.edit.${pageStore.currentPage?.id}`)
          return true;
      });
      return false;
    });
    function canEditElement(elementId) {
      if (canEditPageSettings.value)
        return true;
      const permissionsThatMatch = currentUser?.all_permissions.find((permission) => {
        if (permission.name.toLowerCase() === "elements.*")
          return true;
        if (permission.name.toLowerCase() === "elements.edit.*")
          return true;
        if (permission.name.toLowerCase() === `elements.edit.${elementId}`)
          return true;
      });
      return !!permissionsThatMatch;
    }
    return {
      props,
      pageStore,
      adminStore,
      showAction,
      selectElement,
      multiSelectElement,
      openElement,
      closeElement,
      openAndSelect,
      findElement,
      makeRecipeFromElement,
      makeRecipeFromPage,
      setSliceElement,
      setGroupElement,
      setQuerySliceElement,
      dropTopOfDataArray,
      dropMiddleOfDataArray,
      contextMenuHandler,
      unsetContextMenu,
      duplicateElement,
      cancelCopyElementToPage,
      enableElement,
      disableElement,
      deleteElement,
      canEditPageSettings,
      canEditElement
    };
  }
});
</script>

<template>
  <div v-if="pageStore.currentPage">
    <VoixDrop
      v-slot="{ item }"
      :entire-data-array="pageStore.currentPage.elements"
      :data-array="props.elements"
      data-testid="voix-page-navigation-elements"
      children-key="elements"
      class="flex flex-col space-y-1"
      @drop-at-top="dropTopOfDataArray"
      @drop-in-middle="dropMiddleOfDataArray"
    >
      <PageNavigationSlice
        v-if="item.type === 'slice'"
        :id="`${item.id?.toString()}`"
        :key="item.id?.toString()"
        :slice="setSliceElement(item)"
        :depth="depth"
        data-testid="voix-page-navigation-element"
        :class="{ 'opacity-50 pointer-events-none select-none': !canEditPageSettings && !canEditElement(item.id) }"
        @select-element="selectElement($event)"
        @multi-select-element="multiSelectElement($event)"
        @mouseenter="adminStore.setHighlightedElement(item)"
        @mouseleave="adminStore.unsetHighlightedElement()"
        @context="contextMenuHandler"
      />

      <PageNavigationQuery
        v-if="item.type === 'query'"
        :id="`${item.id?.toString()}`"
        :key="item.id"
        :query-slice="setQuerySliceElement(item)"
        :depth="depth"
        data-testid="voix-page-navigation-query"
        :class="{ 'opacity-50 pointer-events-none select-none': !canEditPageSettings && !canEditElement(item.id) }"
        @select-element="selectElement(item)"
        @multi-select-element="multiSelectElement(item)"
        @find-item="findElement($event.target.value)"
        @mouseenter="adminStore.setHighlightedElement(item)"
        @mouseleave="adminStore.unsetHighlightedElement()"
        @context="contextMenuHandler"
      />

      <PageNavigationGroup
        v-if="item.type === 'group'"
        :id="`${item.id?.toString()}`"
        :key="item.id"
        :group="setGroupElement(item)"
        :depth="depth"
        data-testid="voix-page-navigation-group"
        :class="{ 'opacity-50 pointer-events-none select-none': !canEditPageSettings && !canEditElement(item.id) }"
        @select-element="selectElement(item)"
        @multi-select-element="multiSelectElement(item)"
        @click.left.exact="openElement(item)"
        @mouseenter="adminStore.setHighlightedElement(item)"
        @mouseleave="adminStore.unsetHighlightedElement()"
        @context="contextMenuHandler"
      />
    </VoixDrop>

    <template v-if="adminStore.currentContextItem.element">
      <Teleport to="#voix-admin-root">
        <div class="fixed inset-0" @click="unsetContextMenu" />

        <CopySliceToPage
          v-if="showAction === 'copyElementToPage'"
          :slice="adminStore.currentContextItem.element"
          :slice-name="adminStore.currentContextItem.element.component"
          class="fixed w-96 bg-gray-100 p-4 rounded text-sm shadow-2xl"
          :style="{
            top: `${adminStore.currentContextItem.y}px`,
            left: `${adminStore.currentContextItem.x}px`,
          }"
          @close="cancelCopyElementToPage"
        />

        <ul
          v-else
          class="fixed bg-white rounded-sm flex flex-col divide-y shadow-lg text-xs" :style="{
            top: `${adminStore.currentContextItem.y}px`,
            left: `${adminStore.currentContextItem.x}px`,
          }"
        >
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="openAndSelect(adminStore.currentContextItem.element)">
              <svg
                v-voix-popper:#app.top="`<span class='text-xs'>A Slice</span>`"
                class="w-3 h-3 flex-none"
                xmlns="http://www.w3.org/2000/svg"
                width="9.576"
                height="9.576"
                viewBox="0 0 9.576 9.576"
              >
                <path
                  fill="currentColor"
                  class="cls-1"
                  d="M2.972,0a.617.617,0,0,0-.661.451L2.034,1.506A5.894,5.894,0,0,1,8.066,7.435l1.065-.3a.617.617,0,0,0,.442-.671A7.433,7.433,0,0,0,2.972,0ZM1.879,2.1.011,9.2a.3.3,0,0,0,.374.369L7.474,7.6a5.356,5.356,0,0,0-5.6-5.5Zm.516,5.683a.6.6,0,1,1,.6-.6A.6.6,0,0,1,2.395,7.781Zm.9-2.843a.6.6,0,1,1,.6-.6A.6.6,0,0,1,3.293,4.938ZM5.238,6.883a.6.6,0,1,1,.6-.6A.6.6,0,0,1,5.238,6.883Z"
                  transform="translate(-0.001 0)"
                />
              </svg>
              <span>Element Properties</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="findElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:document-magnifying-glass" />
              <span>Find Element</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="duplicateElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:document-duplicate" />

              <span>Duplicate Element</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="showAction = 'copyElementToPage'">
              <Icon class="w-4 h-4" name="heroicons:document-plus" />

              <span>Copy Element To Page</span>
            </button>
          </li>
          <li v-if="!adminStore.currentContextItem.element.enabled">
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="enableElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:eye" />
              <span>Enable Element</span>
            </button>
          </li>
          <li v-if="adminStore.currentContextItem.element.enabled">
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="disableElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:eye-slash" />
              <span>Disable Element</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="deleteElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:trash" />
              <span>Remove Element</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="makeRecipeFromPage()">
              <Icon class="w-4 h-4" name="heroicons:wrench" />
              <span>Developer: Make Recipe from Page</span>
            </button>
          </li>
          <li>
            <button class="px-4 py-2 w-full text-left flex items-center space-x-2 voix-admin-text-dark hover:voix-admin-text duration-200" @click="makeRecipeFromElement(adminStore.currentContextItem.element)">
              <Icon class="w-4 h-4" name="heroicons:wrench" />
              <span>Developer: Make Recipe from Element</span>
            </button>
          </li>
        </ul>
      </Teleport>
    </template>
  </div>
</template>
